import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    AssessorAccess,
    AssessorAccessRequest,
    AssessorAccessResponse,
    AssessorUsersSearchResponse,
    AssessorVesselGroupsSearchResponse,
    UpsertAssessorAccessRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/crewAndShoreAssessor.api.ts';
import {
    createApiAssessorAccess,
    deleteApiAssessorAccess,
    getApiAssessorAccess,
    getApiAssessorUsers,
    getApiAssessorVesselGroups,
    updateApiAssessorAccess,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/crewAndShoreAssessor.api.ts';
import { extractError } from '@utils/slice.ts';

interface ShoreAssessorError {
    message?: string;
    [key: string]: any;
}

interface ShoreAssessorState {
    error: ShoreAssessorError | null;
    shoreAssessorsData: AssessorAccessResponse | null;
    shoreAssessorVesselGroupsData: AssessorVesselGroupsSearchResponse | null;
    shoreAssessorUsersData: AssessorUsersSearchResponse | null;
    isGetShoreAssessorsPending: boolean;
    isCreateShoreAssessorPending: boolean;
    isUpdateShoreAssessorPending: boolean;
    isDeleteShoreAssessorPending: boolean;
    isGetShoreAssessorVesselGroupsPending: boolean;
    isGetShoreAssessorUsersPending: boolean;
}

const initialState: ShoreAssessorState = {
    error: null,
    shoreAssessorsData: null,
    shoreAssessorVesselGroupsData: null,
    shoreAssessorUsersData: null,
    isGetShoreAssessorsPending: false,
    isCreateShoreAssessorPending: false,
    isUpdateShoreAssessorPending: false,
    isDeleteShoreAssessorPending: false,
    isGetShoreAssessorVesselGroupsPending: false,
    isGetShoreAssessorUsersPending: false,
};

export const getShoreAssessors = createAsyncThunk<
    AssessorAccessResponse,
    { tenantAlias: string; payload: AssessorAccessRequest },
    { rejectValue: ShoreAssessorError }
>('shoreAssessor/getShoreAssessors', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiAssessorAccess(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createShoreAssessor = createAsyncThunk<
    AssessorAccess,
    { tenantAlias: string; payload: UpsertAssessorAccessRequest },
    { rejectValue: ShoreAssessorError }
>('shoreAssessor/createShoreAssessor', ({ tenantAlias, payload }, { rejectWithValue }) =>
    createApiAssessorAccess(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateShoreAssessor = createAsyncThunk<
    AssessorAccess,
    { tenantAlias: string; accessId: number; payload: UpsertAssessorAccessRequest },
    { rejectValue: ShoreAssessorError }
>('shoreAssessor/updateShoreAssessor', ({ tenantAlias, accessId, payload }, { rejectWithValue }) =>
    updateApiAssessorAccess(tenantAlias, accessId, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteShoreAssessor = createAsyncThunk<
    any,
    { tenantAlias: string; accessId: number },
    { rejectValue: ShoreAssessorError }
>('shoreAssessor/deleteShoreAssessor', ({ tenantAlias, accessId }, { rejectWithValue }) =>
    deleteApiAssessorAccess(tenantAlias, accessId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getShoreAssessorVesselGroups = createAsyncThunk<
    AssessorVesselGroupsSearchResponse,
    { tenantAlias: string; payload: AssessorAccessRequest },
    { rejectValue: ShoreAssessorError }
>('shoreAssessor/getShoreAssessorVesselGroups', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiAssessorVesselGroups(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getShoreAssessorUsers = createAsyncThunk<
    AssessorUsersSearchResponse,
    { tenantAlias: string; payload: AssessorAccessRequest },
    { rejectValue: any }
>('shoreAssessor/getShoreAssessorUsers', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiAssessorUsers(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const shoreAssessorSlice = createSlice({
    name: 'shoreAssessor',
    initialState,
    reducers: {
        clearShoreAssessorRoles: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(getShoreAssessors.pending, (state) => {
            state.isGetShoreAssessorsPending = true;
            state.error = null;
        });
        builder.addCase(getShoreAssessors.fulfilled, (state, action) => {
            state.isGetShoreAssessorsPending = false;
            state.shoreAssessorsData = action.payload;
        });
        builder.addCase(getShoreAssessors.rejected, (state, action) => {
            state.isGetShoreAssessorsPending = false;
            state.error = extractError(action);
        });

        builder.addCase(createShoreAssessor.pending, (state) => {
            state.isCreateShoreAssessorPending = true;
            state.error = null;
        });
        builder.addCase(createShoreAssessor.fulfilled, (state) => {
            state.isCreateShoreAssessorPending = false;
        });
        builder.addCase(createShoreAssessor.rejected, (state, action) => {
            state.isCreateShoreAssessorPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateShoreAssessor.pending, (state) => {
            state.isUpdateShoreAssessorPending = true;
            state.error = null;
        });
        builder.addCase(updateShoreAssessor.fulfilled, (state) => {
            state.isUpdateShoreAssessorPending = false;
        });
        builder.addCase(updateShoreAssessor.rejected, (state, action) => {
            state.isUpdateShoreAssessorPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteShoreAssessor.pending, (state) => {
            state.isDeleteShoreAssessorPending = true;
            state.error = null;
        });
        builder.addCase(deleteShoreAssessor.fulfilled, (state) => {
            state.isDeleteShoreAssessorPending = false;
        });
        builder.addCase(deleteShoreAssessor.rejected, (state, action) => {
            state.isDeleteShoreAssessorPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getShoreAssessorVesselGroups.pending, (state) => {
            state.isGetShoreAssessorVesselGroupsPending = true;
            state.error = null;
        });
        builder.addCase(getShoreAssessorVesselGroups.fulfilled, (state, action) => {
            state.isGetShoreAssessorVesselGroupsPending = false;
            state.shoreAssessorVesselGroupsData = action.payload;
        });
        builder.addCase(getShoreAssessorVesselGroups.rejected, (state, action) => {
            state.isGetShoreAssessorVesselGroupsPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getShoreAssessorUsers.pending, (state) => {
            state.isGetShoreAssessorUsersPending = true;
            state.error = null;
        });
        builder.addCase(getShoreAssessorUsers.fulfilled, (state, action) => {
            state.isGetShoreAssessorUsersPending = false;
            state.shoreAssessorUsersData = action.payload;
        });
        builder.addCase(getShoreAssessorUsers.rejected, (state, action) => {
            state.isGetShoreAssessorUsersPending = false;
            state.error = extractError(action);
        });
    },
});

export const { clearShoreAssessorRoles } = shoreAssessorSlice.actions;

export default shoreAssessorSlice.reducer;
